/**
 * Score Preview card shown above the FillQuestionnaire form. Hidden
 * from suppliers because it surfaces the internal pass/fail signal
 * the customer sees — extracted from FillQuestionnaire.tsx so that
 * page stays close to the 500-line cap.
 */
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Target, Loader2 } from 'lucide-react';
import type { ScorePreview } from '../../lib/api';

interface ScorePreviewCardProps {
  scorePreview: ScorePreview;
  loading: boolean;
}

export default function ScorePreviewCard({ scorePreview, loading }: ScorePreviewCardProps) {
  return (
    <Card className={`border-l-4 ${
      scorePreview.risk_level === 'low' ? 'border-l-green-500' :
      scorePreview.risk_level === 'medium' ? 'border-l-yellow-500' :
      scorePreview.risk_level === 'high' ? 'border-l-orange-500' :
      'border-l-red-500'
    }`}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">Score Preview</span>
          </div>
          <Badge variant={scorePreview.projected_passed ? 'default' : 'destructive'} className="text-xs">
            {scorePreview.projected_passed ? 'On Track to Pass' : 'Below Pass Threshold'}
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary">{scorePreview.score_percentage.toFixed(0)}%</p>
            <p className="text-xs text-muted-foreground">Current Score</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{scorePreview.pass_score}%</p>
            <p className="text-xs text-muted-foreground">Pass Threshold</p>
          </div>
          <div>
            <Badge variant="outline" className={`text-xs ${
              scorePreview.risk_level === 'low' ? 'text-green-600 border-green-300' :
              scorePreview.risk_level === 'medium' ? 'text-yellow-600 border-yellow-300' :
              scorePreview.risk_level === 'high' ? 'text-orange-600 border-orange-300' :
              'text-red-600 border-red-300'
            }`}>
              {scorePreview.risk_display.label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">Risk Level</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>{scorePreview.questions_answered} of {scorePreview.total_scored_questions} scored questions answered</span>
          {loading && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
      </CardContent>
    </Card>
  );
}
